import { CatalogArticleSearchResult } from "@albertine/types";
import { ImageThumbnail } from "@prototypes/image-thumbnail/ImageThumbnail";
import classNames from "classnames";
import { useEffect, useMemo } from "react";
import { isNumber } from "remeda";
import { locationString } from "../../common/utils/catalog.util";
import { cdnImage } from "../../common/utils/image.util";
import { EventEmitter, type EventListener } from "../../EventEmitter";
import {
    useCatalogSelectionUrlParams,
    useComponentState,
} from "../../utils/customHook.util";
import { FlexInline } from "../Common/Flex";
import Label from "../Common/Label";
import Tag from "../Common/Tag";
import "./SearchResultItem.css";
import {
    type CatalogItem,
    CatalogItemSearchResultSchema,
    type SearchCatalogItemsQuery,
} from "@albertine/types/openapi/lmt";
import { Lib } from "albertine-shared-web";
import { catalogItemToArticleLocation } from "../../common/CatalogItem";
import { categoryDisplayName } from "../../common/types/OfferingCategory";

type SearchResultItemProps = {
    className?: string;
    item: CatalogItem;
    fitScore?: number;
    filters: SearchCatalogItemsQuery;
    onClick: (item: CatalogItem) => void;
    onHover?: (id: string | undefined) => void;
    highlightedItemId?: string;
    selectedItemId?: string;
};

type State = {
    isDeleted: boolean;
    item: CatalogItem;
};

function SearchResultItem(props: SearchResultItemProps) {
    const {
        className,
        item,
        fitScore,
        filters,
        onClick,
        onHover,
        highlightedItemId,
        selectedItemId,
    } = props;
    const selection = useCatalogSelectionUrlParams();
    const { state, setState } = useComponentState<State>({
        isDeleted: false,
        item,
    });

    useEffect(() => {
        const editedListener: EventListener = {
            name: "catalogItemEdited",
            listener: (params) => {
                if (params.item.id !== state.item.id) {
                    return;
                }

                setState({
                    item: {
                        ...state.item,
                        name: params.item.name,
                        images: params.item.images ?? [],
                        offeringCategories: params.item.offeringCategories ?? [],
                        addressComponents: params.item.addressComponents ?? [],
                        locationGeoPoint: params.item.locationGeoPoint,
                    },
                });
            },
        };

        const deleteListener: EventListener = {
            name: "catalogItemDeleted",
            listener: (params) => {
                const deleted = params.item === state.item.id;

                if (deleted) {
                    setState({
                        isDeleted: true,
                    });
                }
            },
        };

        EventEmitter.addListener(editedListener);
        EventEmitter.addListener(deleteListener);

        return () => {
            EventEmitter.removeListener(editedListener);
            EventEmitter.removeListener(deleteListener);
        };
    }, [state.item]);

    const isDistanceSortingApplied = useMemo(() => {
        const hasBounds = !!(
            filters.geoBoundingBoxTop &&
            filters.geoBoundingBoxBottom &&
            filters.geoBoundingBoxLeft &&
            filters.geoBoundingBoxRight
        );

        const hasLocationFilter =
            filters.latitude && filters.longitude && filters.maxGeoDistanceKm;

        return hasBounds || hasLocationFilter;
    }, [
        filters.geoBoundingBoxTop,
        filters.geoBoundingBoxBottom,
        filters.geoBoundingBoxLeft,
        filters.geoBoundingBoxRight,
        filters.latitude,
        filters.longitude,
        filters.maxGeoDistanceKm,
    ]);

    const distanceText = useMemo(() => {
        const parsed = CatalogItemSearchResultSchema.safeParse(state.item);
        const distanceInKm = parsed.success
            ? parsed.data.distanceFromLocationInKm
            : undefined;
        if (isNumber(distanceInKm)) {
            if (distanceInKm < 0.1) {
                return "<100 m";
            }

            if (distanceInKm < 1) {
                return `${Math.round(distanceInKm * 10) * 100} m`;
            }

            return `${Math.round(distanceInKm)} km`;
        }
        return "-";
    }, [state.item]);

    return state.isDeleted ? null : (
        <Lib.Flex.Row
            gap="value16"
            alignItems="start"
            className={classNames(
                "search-result-item",
                {
                    "search-result-item--selected":
                        selectedItemId !== undefined
                            ? selectedItemId === state.item.id
                            : selection.urlState.searchCatalogId === state.item.id ||
                              selection.urlState.searchCatalogId === state.item.lmtId,
                    "search-result-item--highlighted":
                        highlightedItemId === state.item.id ||
                        highlightedItemId === state.item.lmtId,
                },
                className,
            )}
            onClick={() => {
                onClick(state.item);
            }}
            onMouseEnter={() => {
                onHover?.(state.item.id);
            }}
            onMouseLeave={() => {
                onHover?.(undefined);
            }}
        >
            <ImageThumbnail
                src={
                    state.item.images[0]
                        ? cdnImage(state.item.images[0].url, {
                              height: 64,
                              width: 64,
                          })
                        : undefined
                }
                variant="missing"
                className="size-14 shrink-0 rounded-md"
            />
            <Lib.Flex.Column
                gap="value8"
                justifyContent="start"
                className="search-result-item__details"
            >
                <div className="flex w-full items-start justify-between gap-3">
                    <h4>{state.item.name}</h4>

                    {!!fitScore && (
                        <div className="shrink-0 pt-0.5">
                            <span className="font-medium text-xs text-zinc-900 leading-5">
                                {Math.round(fitScore * 10)}%
                            </span>
                        </div>
                    )}
                </div>

                <div className="search-result-item__categories">
                    <Label.Medium.Light.N50>
                        {state.item.offeringCategories
                            ?.map((category) => categoryDisplayName(category, "en"))
                            .join(", ")}
                    </Label.Medium.Light.N50>
                </div>
                <FlexInline gap="small" alignSelf="start">
                    {isDistanceSortingApplied && (
                        <Tag.XSmall.Bold.T90>{distanceText}</Tag.XSmall.Bold.T90>
                    )}
                    <Label.Small.SemiBold>
                        {locationString({
                            location: catalogItemToArticleLocation({
                                addressComponents: state.item.addressComponents,
                                geoPoint: state.item.locationGeoPoint,
                            }).location,
                        })}
                    </Label.Small.SemiBold>
                </FlexInline>
            </Lib.Flex.Column>
        </Lib.Flex.Row>
    );
}

export default SearchResultItem;
